import React, { useState, useEffect, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';

interface MapLocation {
    id: string;
    type: 'waiting' | 'inTransit' | 'delivered' | 'withoutTransport';
    coordinates: string | null;
    label: string | null;
    clientName: string | null;
    projectName: string | null;
    city: string | null;
    address: string | null;
    transportId: number | null;
}

interface Props {
    locations: MapLocation[];
    onMarkerClick?: (item: MapLocation) => void;
    onCreateTransport?: () => void;
}

const PIN_VISUAL: Record<string, { color: string; id: string; defaultMessage: string }> = {
    waiting:          { color: '#f59e0b', id: 'transport.strip.waiting',          defaultMessage: 'Në pritje' },
    inTransit:        { color: '#3b82f6', id: 'transport.strip.inTransit',        defaultMessage: 'Në transport' },
    delivered:        { color: '#22c55e', id: 'transport.strip.delivered',        defaultMessage: 'Të dërguara' },
    withoutTransport: { color: '#94a3b8', id: 'transport.strip.withoutTransport', defaultMessage: 'Pa transport' },
};

type StatusKey = 'waiting' | 'inTransit' | 'delivered' | 'withoutTransport';

const createIcon = (color: string) => L.divIcon({
    html: `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="40" viewBox="0 0 28 40">
        <path d="M14 0C6.27 0 0 6.27 0 14c0 10.5 14 26 14 26s14-15.5 14-26C28 6.27 21.73 0 14 0z" fill="${color}" stroke="#fff" stroke-width="2"/>
        <circle cx="14" cy="14" r="5.5" fill="#fff"/>
    </svg>`,
    className: 'transport-map-marker',
    iconSize: [28, 40],
    iconAnchor: [14, 40],
    popupAnchor: [0, -40],
});

const icons: Record<string, L.DivIcon> = {
    waiting: createIcon(PIN_VISUAL.waiting.color),
    inTransit: createIcon(PIN_VISUAL.inTransit.color),
    delivered: createIcon(PIN_VISUAL.delivered.color),
    withoutTransport: createIcon(PIN_VISUAL.withoutTransport.color),
};

const parseCoordinates = (coords: string | null): [number, number] | null => {
    if (!coords) return null;
    const parts = coords.split(',').map(s => parseFloat(s.trim()));
    if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) return [parts[0], parts[1]];
    return null;
};

const TransportMapView: React.FC<Props> = ({ locations, onMarkerClick, onCreateTransport }) => {
    const intl = useIntl();
    const fm = (id: string, defaultMessage: string, values?: Record<string, any>) =>
        intl.formatMessage({ id, defaultMessage }, values);

    const [activeFilters, setActiveFilters] = useState<Record<StatusKey, boolean>>({
        waiting: true, inTransit: true, delivered: true, withoutTransport: true,
    });

    const toggleFilter = (key: StatusKey) => {
        setActiveFilters(prev => ({ ...prev, [key]: !prev[key] }));
    };

    // Count all with valid coordinates by type
    const counts: Record<string, number> = { waiting: 0, inTransit: 0, delivered: 0, withoutTransport: 0 };
    (locations || []).forEach(loc => {
        if (loc.coordinates && parseCoordinates(loc.coordinates)) {
            counts[loc.type] = (counts[loc.type] || 0) + 1;
        }
    });

    // Filter markers
    const markers: Array<{ position: [number, number]; item: MapLocation }> = [];
    (locations || []).forEach(loc => {
        if (!loc.coordinates || !activeFilters[loc.type as StatusKey]) return;
        const pos = parseCoordinates(loc.coordinates);
        if (!pos) return;
        markers.push({ position: pos, item: loc });
    });

    const defaultCenter: [number, number] = markers.length > 0 ? markers[0].position : [42.6629, 21.1655];

    return (
        <div style={{ flex: 1, minHeight: 0, borderRadius: 8, overflow: 'hidden', position: 'relative' }}>
            <MapContainer center={defaultCenter} zoom={8} style={{ height: '100%', width: '100%' }} scrollWheelZoom={true}>
                <TileLayer
                    attribution='&copy; <a href="https://carto.com/">CARTO</a>'
                    url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                />
                {markers.map((m) => (
                    <Marker key={m.item.id} position={m.position} icon={icons[m.item.type] || icons.waiting}
                        eventHandlers={{ click: () => onMarkerClick && onMarkerClick(m.item) }}>
                        <Popup>
                            <div style={{ minWidth: 180 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: PIN_VISUAL[m.item.type]?.color, flexShrink: 0 }} />
                                    <span style={{ fontSize: 10, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                                        {PIN_VISUAL[m.item.type] ? fm(PIN_VISUAL[m.item.type].id, PIN_VISUAL[m.item.type].defaultMessage) : ''}
                                    </span>
                                </div>
                                {m.item.label && <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a', marginBottom: 4 }}>{m.item.label}</div>}
                                {m.item.clientName && <div style={{ fontSize: 12, color: '#475569', marginBottom: 2 }}>{m.item.clientName}</div>}
                                {m.item.projectName && <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 2 }}>{m.item.projectName}</div>}
                                {(m.item.address || m.item.city) && <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>{m.item.address || m.item.city}</div>}
                                <div style={{ display: 'flex', gap: 8, marginTop: 8, borderTop: '1px solid #e2e8f0', paddingTop: 8 }}>
                                    {m.item.transportId && (
                                        <a href={`/dashboard/transport/${m.item.transportId}`}
                                            style={{ fontSize: 11, color: '#2a5a8a', textDecoration: 'none', fontWeight: 600 }}>
                                            <FormattedMessage id="transport.map.viewTransport" defaultMessage="Shiko transportin" />
                                        </a>
                                    )}
                                    {!m.item.transportId && m.item.type === 'waiting' && onCreateTransport && (
                                        <button onClick={onCreateTransport}
                                            style={{ fontSize: 11, color: '#2a5a8a', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 600 }}>
                                            <FormattedMessage id="transport.map.createTransport" defaultMessage="Krijo transport" />
                                        </button>
                                    )}
                                    <a href={`https://www.google.com/maps/dir/?api=1&destination=${m.position[0]},${m.position[1]}`}
                                        target="_blank" rel="noopener noreferrer"
                                        style={{ fontSize: 11, color: '#64748b', textDecoration: 'none', marginLeft: 'auto' }}>
                                        <FormattedMessage id="transport.map.googleMaps" defaultMessage="Google Maps →" />
                                    </a>
                                </div>
                            </div>
                        </Popup>
                    </Marker>
                ))}
            </MapContainer>

            {/* Filter toggles */}
            <div style={{
                position: 'absolute', top: 12, right: 12, zIndex: 1000,
                background: '#fff', borderRadius: 10, padding: 6,
                boxShadow: '0 2px 16px rgba(0,0,0,0.12)', border: '1px solid #e2e8f0',
                display: 'flex', flexDirection: 'column', gap: 2, minWidth: 160,
            }}>
                {(Object.keys(PIN_VISUAL) as StatusKey[]).map((key) => {
                    const cfg = PIN_VISUAL[key];
                    const active = activeFilters[key];
                    const count = counts[key] || 0;
                    return (
                        <button key={key} onClick={() => toggleFilter(key)}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 10,
                                padding: '7px 12px', border: 'none', borderRadius: 6,
                                background: active ? '#f8fafc' : 'transparent',
                                cursor: 'pointer', transition: 'all 0.15s',
                                opacity: active ? 1 : 0.35,
                                width: '100%', textAlign: 'left',
                            }}>
                            <div style={{
                                width: 12, height: 12, borderRadius: '50%', flexShrink: 0,
                                background: cfg.color,
                                boxShadow: active ? `0 0 0 2px #fff, 0 0 0 3.5px ${cfg.color}` : 'none',
                                transition: 'box-shadow 0.15s',
                            }} />
                            <span style={{ fontSize: 12, color: '#0f172a', fontWeight: 500, flex: 1 }}>{fm(cfg.id, cfg.defaultMessage)}</span>
                            <span style={{ fontSize: 11, fontWeight: 600, minWidth: 24, textAlign: 'right', color: active ? '#0f172a' : '#94a3b8' }}>{count}</span>
                        </button>
                    );
                })}
                <div style={{ borderTop: '1px solid #e2e8f0', margin: '4px 0 2px', padding: '6px 12px 2px' }}>
                    <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500 }}>{fm('transport.map.pointsInMap', '{count} pika në hartë', { count: markers.length })}</span>
                </div>
            </div>
        </div>
    );
};

export default TransportMapView;
